import './translations';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useNavigate } from 'react-router-dom';
import { Box, Button, CircularProgress, Grid, Stack, Typography } from '@mui/material';
import { Breadcrumb } from 'components/layout/Breadcrumb';
import { useGetBlueprints } from 'features/blueprints/hooks/useGetBlueprints';
import { useFavouriteBlueprints } from 'features/favourites/hooks/useFavouriteBlueprints/useFavouriteBlueprints';
import { BlueprintsFilterSection } from 'features/filters/components/BlueprintsFilterSection';
import { useGetOrganisation } from 'features/organisations/hooks/useGetOrganisation';
import { Icons } from 'greener-ui-library';
import { useAmplitude } from 'hooks/useAmplitude';
import useAuth from 'hooks/useAuth';
import { useBreakpoint } from 'hooks/useBreakpoint';
import { debounce } from 'lodash';
import { useIntersectionObserver } from 'usehooks-ts';
import { canCreateBlueprints, isGreenerEmployee } from 'utils/authorization';
import { blueprintsOverviewRoutes } from './Blueprints.routes';

const BLUEPRINTS_PER_PAGE_MAP = {
  xs: 3,
  sm: 6,
  md: 12,
  lg: 18,
  xl: 42,
};

export const BlueprintsTopbar = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { width, isMobile } = useBreakpoint();
  const { user } = useAuth();
  const { logEvent } = useAmplitude();
  const organisationId = user?.organisation?.organisationId?.toString() ?? '';
  const { organisation } = useGetOrganisation(organisationId);

  // We do not show the logo and "partner" text when the user is a Greener employee.
  const shouldShowPartnerLogo = !isGreenerEmployee(user);

  const { blueprints, isLoading, isFetching, goToNextPage } = useGetBlueprints({
    perPage: BLUEPRINTS_PER_PAGE_MAP[width],
    orderBy: 'ts_created',
    order: 'desc',
  });

  const isLoadingOrFetching = isLoading || isFetching;

  const { ref, isIntersecting } = useIntersectionObserver({
    threshold: 1.0,
  });

  const { favouriteBlueprints } = useFavouriteBlueprints();

  useEffect(() => {
    const debouncedHandler = debounce((isIntersecting: boolean) => {
      if (isIntersecting && !isLoadingOrFetching && !!goToNextPage) {
        goToNextPage();
      }
    }, 200);

    if (isIntersecting) {
      debouncedHandler(isIntersecting);
    }

    return () => {
      debouncedHandler.cancel();
    };
  }, [isIntersecting, isLoadingOrFetching, goToNextPage]);

  return (
    <Grid container direction="row">
      <Breadcrumb routes={blueprintsOverviewRoutes} />
      <Grid item container alignItems="center" justifyContent="space-between">
        <Grid item>
          <Typography variant="h1">{t('entities:blueprint.namePlural')}</Typography>
        </Grid>
        <Grid item sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {shouldShowPartnerLogo && (
            <>
              <Typography
                variant="caption"
                sx={{
                  color: 'grey.800',
                  lineHeight: 1.66,
                  letterSpacing: '0.03333em',
                }}
              >
                {t('blueprints:overview.partner')}
              </Typography>
              {organisation?.logoUrl ? (
                <Box
                  component="img"
                  src={organisation.logoUrl}
                  alt={organisation.name}
                  sx={{
                    width: '91px',
                    height: '42.523px',
                    objectFit: 'contain',
                  }}
                />
              ) : (
                <Box
                  sx={{
                    width: '91px',
                    height: '42.523px',
                    bgcolor: 'grey.100',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'grey.500',
                    fontSize: '12px',
                  }}
                >
                  -
                </Box>
              )}
            </>
          )}
        </Grid>
      </Grid>
      <Grid
        container
        item
        py={4}
        spacing={2}
        alignItems={isMobile ? 'center' : 'flex-start'}
        justifyContent={'space-between'}
      >
        <Grid item xs>
          <BlueprintsFilterSection />
        </Grid>
        {canCreateBlueprints(user?.role) && (
          <Grid item>
            <Button
              variant="outlined"
              data-testid="new-blueprint-button"
              onClick={() => {
                logEvent({
                  eventType: 'solution_started',
                  eventProperties: { user_type: isGreenerEmployee(user) ? 'greener' : 'partner' },
                });
                navigate('/solutions/new');
              }}
              sx={isMobile ? { minWidth: 0, p: 1, mt: -5 } : null}
            >
              {isMobile ? <Icons.Plus /> : t('blueprints:overview.buttons.newBlueprint')}
            </Button>
          </Grid>
        )}
      </Grid>
      <Outlet context={{ blueprints, favouriteBlueprints: favouriteBlueprints?.data ?? [], ref, isLoading }} />
      <Stack flex={1} p={3}>
        <CircularProgress
          sx={{
            visibility: isLoadingOrFetching ? 'visible' : 'hidden',
            alignSelf: 'center',
          }}
          size={20}
        />
      </Stack>
      <div
        ref={ref}
        style={{
          height: '1px',
          width: '100%',
        }}
      />
    </Grid>
  );
};
